import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import VideoThumbnail from "../../videoplayer/VideoThumbnail";
import { useState, useRef, useEffect } from "react";

interface GalleryImage {
  src?: string;
  alt?: string;
  caption?: string;
  type?: 'video' | 'diagram' | 'image';
  videoId?: string;
  title?: string;
  zoomSrc?: string;
  thumbSrc?: string;
}

interface ImageGalleryProps {
  images: GalleryImage[];
  delay?: number;
}

export default function ImageGallery({ images, delay = 0 }: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [direction, setDirection] = useState(0); // -1 für links, 1 für rechts
  
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);

  // Filter-Funktion, um nur Nicht-Video-Images zu bekommen
  const nonVideoImages = images.filter(image => image.type !== 'video');
  const hasNonVideoImages = nonVideoImages.length > 0;
  
  // Funktion, um das richtige Hauptbild zu erhalten
  const getMainImageSrc = () => {
    if (activeIndex >= images.length) return "";
    return images[activeIndex].src || "";
  };
  
  // Funktion, um das richtige Thumbnail zu erhalten
  const getThumbSrc = (image: GalleryImage) => {
    return image.thumbSrc || image.src || "";
  };
  
  // Funktion, um das richtige Zoom-Bild zu erhalten
  const getLightboxImageSrc = (index: number) => {
    if (index >= images.length) return "";
    const image = images[index];
    return image.zoomSrc || image.src || "";
  };
  
  // Handler für Thumbnail-Klicks
  const handleThumbnailClick = (index: number) => {
    // Nach rechts navigieren (nächstes Bild) wenn index > activeIndex
    setDirection(index > activeIndex ? 1 : -1);
    
    // Sofortige Änderung ohne Verzögerung
    setActiveIndex(index);
  };
  
  // Navigation zum vorherigen Bild
  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation(); // Verhindert, dass beim Klick auf den Pfeil die Lightbox geöffnet wird
    if (activeIndex > 0) {
      setDirection(-1); // Nach links navigieren (vorheriges Bild)
      setActiveIndex(prev => prev - 1);
    }
  };
  
  // Navigation zum nächsten Bild
  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation(); // Verhindert, dass beim Klick auf den Pfeil die Lightbox geöffnet wird
    if (activeIndex < images.length - 1) {
      setDirection(1); // Nach rechts navigieren (nächstes Bild)
      setActiveIndex(prev => prev + 1);
    }
  };
  
  // Handler für Hauptbild-Klick (öffnet Lightbox)
  const openLightbox = () => {
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Verhindert Scrollen im Hintergrund
  };
  
  // Handler für Lightbox schließen
  const closeLightbox = () => {
    setIsLightboxOpen(false);
    document.body.style.overflow = ''; // Stellt Scrollen wieder her
  };
  
  // Swipe-Funktionalität
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    setIsDragging(false);
    
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && activeIndex < images.length - 1) {
      setDirection(1); // Nach rechts gehen (nächstes Bild)
      setActiveIndex(prev => prev + 1);
    }
    
    if (isRightSwipe && activeIndex > 0) {
      setDirection(-1); // Nach links gehen (vorheriges Bild)
      setActiveIndex(prev => prev - 1);
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Scroll das aktive Thumbnail in die Mitte
  useEffect(() => {
    if (thumbnailsRef.current) {
      const container = thumbnailsRef.current;
      const activeThumb = container.children[activeIndex] as HTMLElement;
      
      if (activeThumb) {
        const scrollLeft = activeThumb.offsetLeft - (container.offsetWidth / 2) + (activeThumb.offsetWidth / 2);
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeIndex]);

  // Animations-Varianten für den Slide-Effekt
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 150 : -150,
      opacity: 0,
      transition: {
        duration: 0.25,
        ease: "easeOut"
      }
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.25,
        delay: 0
      }
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -150 : 150,
      opacity: 0,
      transition: {
        duration: 0.25,
        ease: "easeIn"
      }
    })
  };

  return (
    <motion.div
      className="mb-20"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay }}
    >
      <motion.h2
        className="text-3xl md:text-4xl font-bold mb-8 text-[#ffcc88]"
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: delay + 0.2 }}
      >
        Visuelle Eindrücke
      </motion.h2>

      {/* Main Content Area */}
      <div className="space-y-6 relative overflow-hidden">
        {/* Main Image or Video Container */}
        <div 
          className="relative" 
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Mobile Swipe Indicator - nur auf kleinen Bildschirmen sichtbar */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 pointer-events-none md:hidden">
            <div className="bg-black/30 rounded-full p-2 text-white/60">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="bg-black/30 rounded-full p-2 text-white/60">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Statische Navigationspfeile - auf Mobilgeräten ausgeblendet */}
          {activeIndex > 0 && (
            <div 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 rounded-full p-2 text-white/80 hover:text-white/100 hover:bg-black/70 transition-all cursor-pointer z-10 hidden md:block"
              onClick={goToPrevious}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          )}
          
          {activeIndex < images.length - 1 && (
            <div 
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 rounded-full p-2 text-white/80 hover:text-white/100 hover:bg-black/70 transition-all cursor-pointer z-10 hidden md:block"
              onClick={goToNext}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )}

          {/* Animierte Slides */}
          <AnimatePresence custom={direction} mode="wait" initial={false}>
            <motion.div
              key={activeIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full"
            >
              {images[activeIndex].type === 'video' ? (
                <div className="rounded-xl overflow-hidden shadow-glow-orange-sm border border-[rgba(255,204,136,0.15)]">
                  <VideoThumbnail
                    videoId={images[activeIndex].videoId!}
                    title={images[activeIndex].title!}
                    path={""}
                    vttPath={""}
                  />
                  {images[activeIndex].caption && (
                    <p className="mt-3 text-white/70 text-sm px-4 py-3">{images[activeIndex].caption}</p>
                  )}
                </div>
              ) : (
                <div 
                  className="relative rounded-xl overflow-hidden shadow-glow-orange-sm border border-[rgba(255,204,136,0.15)] cursor-pointer"
                  onClick={openLightbox}
                >
                  <div className="relative aspect-[16/9] w-full">
                    <Image
                      src={getMainImageSrc()}
                      alt={images[activeIndex].alt || ""}
                      fill
                      className="object-contain bg-white/5"
                      sizes="(max-width: 1280px) 100vw, 1200px"
                      priority
                    />
                    
                    {/* Zoom-Indikator */}
                    <div className="absolute top-4 right-4 bg-black/50 rounded-full p-2 text-white/80">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m4-3h-6" />
                      </svg>
                    </div>
                  </div>
                  
                  {images[activeIndex].caption && (
                    <div className="p-4 border-t border-[rgba(255,204,136,0.15)]">
                      <p className="text-white/80">{images[activeIndex].caption}</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
        
        {/* Thumbnails Row */}
        {images.length > 1 && (
          <div 
            ref={thumbnailsRef}
            className="flex space-x-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-[#ffcc88]/20 scrollbar-track-transparent"
          >
            {images.map((image, idx) => (
              <div 
                key={idx}
                className={`relative shrink-0 cursor-pointer transition-all duration-300 ${
                  idx === activeIndex 
                    ? "border-2 border-[#ffcc88]" 
                    : "border border-[rgba(255,204,136,0.15)] opacity-70 hover:opacity-100"
                }`}
                onClick={() => handleThumbnailClick(idx)}
              >
                {/* Thumbnail Image */}
                {image.type === 'video' ? (
                  <div className="relative w-24 h-16 rounded overflow-hidden bg-white/5 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <span className="text-xs text-white absolute bottom-1 w-full text-center truncate px-1">
                      {image.title?.substring(0, 15)}...
                    </span>
                  </div>
                ) : (
                  <div className="relative w-24 h-16 rounded overflow-hidden">
                    <Image
                      src={getThumbSrc(image)}
                      alt={image.alt || ""}
                      fill
                      className="object-cover"
                      sizes="96px"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {isLightboxOpen && hasNonVideoImages && (
        <motion.div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 z-10 text-white/80 hover:text-white bg-black/50 p-2 rounded-full"
            onClick={closeLightbox}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {/* Main Lightbox Image */}
          <div 
            className="flex-1 flex items-center justify-center"
            ref={lightboxRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <motion.div
              className="relative w-full h-full flex items-center justify-center p-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Image
                src={getLightboxImageSrc(activeIndex)}
                alt={images[activeIndex].alt || ""}
                fill
                className="object-contain"
                sizes="100vw"
              />
              
              {/* Navigation Arrows */}
              {activeIndex > 0 && (
                <button 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 rounded-full p-3 text-white/80 hover:text-white hover:bg-black/70"
                  onClick={() => setActiveIndex(prev => prev - 1)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              
              {activeIndex < nonVideoImages.length - 1 && (
                <button 
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 rounded-full p-3 text-white/80 hover:text-white hover:bg-black/70"
                  onClick={() => setActiveIndex(prev => prev + 1)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </motion.div>
          </div>
          
          {/* Caption */}
          {images[activeIndex].caption && (
            <div className="p-4 text-center">
              <p className="text-white/80 max-w-2xl mx-auto">
                {images[activeIndex].caption}
              </p>
            </div>
          )}
          
          {/* Lightbox Thumbnails */}
          <div className="px-4 py-3 border-t border-white/10">
            <div className="flex justify-center space-x-2 overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent pb-2 max-w-3xl mx-auto">
              {nonVideoImages.map((image, idx) => (
                <div 
                  key={idx}
                  className={`relative cursor-pointer transition-all duration-300 ${
                    nonVideoImages.indexOf(image) === activeIndex 
                      ? "border-2 border-white" 
                      : "border border-white/20 opacity-50 hover:opacity-80"
                  }`}
                  onClick={() => setActiveIndex(images.indexOf(image))}
                >
                  <div className="relative w-16 h-12 rounded overflow-hidden">
                    <Image
                      src={getThumbSrc(image)}
                      alt={image.alt || ""}
                      fill
                      className="object-cover"
                      sizes="64px"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}